import React, { useState, useEffect } from "react";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { usePublicAuth } from "../context/PublicAuthContext";
import { Link } from "react-router-dom";

/**
 * Speichert ein Release dauerhaft in den persönlichen Favoriten (Supporter-only).
 * Unterscheidet sich bewusst vom VoteButton (Herz = Chart-Vote, monatlich).
 */
export default function FavoriteButton({ releaseId, size = 16, showLabel = false, className = "" }) {
  const { isPublicAuth, publicApi, login, publicUser } = usePublicAuth();
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const isSupporter = publicUser?.is_supporter;

  // Prüfen ob Release bereits gespeichert
  useEffect(() => {
    if (!isPublicAuth || !isSupporter || !releaseId) { setChecked(true); return; }
    publicApi("get", "/me/favorites")
      .then(res => {
        const favs = res.data || [];
        setSaved(favs.some(f => f.item_id === releaseId));
        setChecked(true);
      })
      .catch(() => setChecked(true));
  }, [releaseId, isPublicAuth, isSupporter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = async (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (!isPublicAuth) {
      login({ appState: { returnTo: window.location.pathname } });
      return;
    }
    if (!isSupporter) {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 2500);
      return;
    }
    if (loading) return;

    setLoading(true);
    try {
      if (saved) {
        await publicApi("delete", `/me/favorites/${releaseId}`);
        setSaved(false);
      } else {
        await publicApi("post", "/me/favorites", { item_id: releaseId, item_type: "release" });
        setSaved(true);
      }
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  if (!checked) return null;

  return (
    <div className="relative inline-flex">
      <button
        onClick={handleToggle}
        disabled={loading}
        data-testid={`favorite-btn-${releaseId}`}
        title={
          !isPublicAuth ? "Anmelden zum Speichern" :
          !isSupporter ? "Nur für Supporter" :
          saved ? "Aus Favoriten entfernen" : "In Favoriten speichern"
        }
        className={`flex items-center gap-1.5 transition-all cursor-pointer group ${className}`}
      >
        {saved ? (
          <BookmarkCheck
            size={size}
            className="text-amber-400 transition-colors duration-200"
            strokeWidth={2}
          />
        ) : (
          <Bookmark
            size={size}
            className={`transition-colors duration-200 ${
              isSupporter
                ? "text-zinc-500 group-hover:text-amber-400"
                : "text-zinc-700"
            }`}
            strokeWidth={2}
          />
        )}
        {showLabel && (
          <span className={`text-xs font-semibold ${saved ? "text-amber-400" : "text-zinc-500 group-hover:text-amber-400"}`}>
            {saved ? "Gespeichert" : "Merken"}
          </span>
        )}
      </button>

      {/* Supporter-Upsell Tooltip */}
      {showTooltip && !isSupporter && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-48 text-center">
          <div className="bg-zinc-900 border border-amber-500/20 text-xs text-zinc-300 rounded-xl px-3 py-2 shadow-xl">
            <p className="mb-1 font-semibold text-amber-400">Nur für Supporter</p>
            <Link
              to="/supporter"
              className="text-amber-300 hover:text-amber-200 underline underline-offset-2"
              onClick={e => e.stopPropagation()}
            >
              Jetzt Supporter werden →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
